import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Send,
  BarChart3,
  MailOpen,
  MousePointerClick,
  CircleX,
  CircleCheckBig,
  Eye,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { newslettersApi } from "../utils/api";
import CreateNewsletterModal from "./CreateNewsletterModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

interface Newsletter {
  _id: string;
  id: string;
  title: string;
  subject: string;
  status: string;
  sentAt: string | null;
  createdAt: string;
  content?: string;
}

interface NewsletterStats {
  total: number;
  sent: number;
  failed: number;
  opened: number;
  clicked: number;
}

function formatDateTime12h(input: string) {
  const date = new Date(input);
  const datePart = date.toLocaleDateString();
  const timePart = date
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
    .toUpperCase();
  return `${datePart} ${timePart}`;
}

export function Newsletters() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] =
    useState<Newsletter | null>(null);
  const [selectedStats, setSelectedStats] = useState<NewsletterStats | null>(
    null
  );
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<string>("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      const response = await newslettersApi.getNewsletters(
        currentPage,
        limit,
        searchTerm
      );

      if (response.data && response.data.docs) {
        setNewsletters(response.data.docs);
        setTotalPages(response.data.totalPages || 0);
        setTotal(response.data.totalDocs || 0);
      } else {
        setNewsletters([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      setNewsletters([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, [currentPage, limit, searchTerm]); // We optionally could debounce search

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value: string) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const afterCreateComplete = () => {
    fetchNewsletters();
  };

  const handleSendNewsletter = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to send the "${title}" newsletter? This action cannot be undone.`)) {
      return;
    }
    
    setSendingId(id);
    try {
      const response = await newslettersApi.sendNewsletter(id);
      if (response && response.statusCode === 200) {
        toast.success("Newsletter queuing started successfully");
        fetchNewsletters();
      } else {
        toast.error(response?.message || "Failed to send newsletter");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteNewsletter = async (newsletter: Newsletter) => {
    const newsletterId = getNewsletterId(newsletter);
    if (
      !window.confirm(
        `Are you sure you want to delete "${newsletter.title}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(newsletterId);
    try {
      const response = await newslettersApi.deleteNewsletter(newsletterId);
      if (response && response.statusCode === 200) {
        toast.success(response?.message || "Newsletter deleted successfully");
        fetchNewsletters();
      } else {
        toast.error(response?.message || "Failed to delete newsletter");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setDeletingId(null);
    }
  };

  const getNewsletterId = (newsletter: Newsletter) => newsletter._id || newsletter.id;

  const openNewsletterStats = (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    setSelectedStats(null);
    setStatsError(null);
    setIsStatsOpen(true);
  };

  const buildNewsletterPreviewSrcDoc = (title: string, subject: string, content: string) => `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
          :root { color-scheme: light; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 20px;
            background:
              radial-gradient(circle at top left, rgba(59, 130, 246, 0.12), transparent 35%),
              radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.10), transparent 32%),
              #f8fafc;
            font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            color: #0f172a;
          }
          .shell { max-width: 760px; margin: 0 auto; }
          .card {
            background: #ffffff;
            border: 1px solid rgba(148, 163, 184, 0.35);
            border-radius: 20px;
            box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
            overflow: hidden;
          }
          .hero {
            padding: 24px 24px 20px;
            background: linear-gradient(135deg, rgba(15, 23, 42, 0.03), rgba(59, 130, 246, 0.08));
            border-bottom: 1px solid rgba(148, 163, 184, 0.25);
          }
          .eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #2563eb;
            margin-bottom: 12px;
          }
          .title {
            margin: 0 0 8px;
            font-size: 28px;
            line-height: 1.15;
            letter-spacing: -0.03em;
          }
          .subject {
            margin: 0;
            color: #475569;
            font-size: 15px;
            line-height: 1.6;
          }
          .content { padding: 24px; background: #fff; }
          .content :where(img) { max-width: 100%; height: auto; display: block; }
          .content :where(table) { width: 100%; border-collapse: collapse; }
          .content :where(a) { color: #2563eb; }
          .content :where(h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote) { margin-top: 0; }
          .content :where(p, li, blockquote) { color: #0f172a; line-height: 1.7; }
          .empty {
            padding: 48px 24px;
            text-align: center;
            color: #64748b;
          }
          .empty strong {
            display: block;
            margin-bottom: 8px;
            color: #0f172a;
          }
        </style>
      </head>
      <body>
        <div class="shell">
          <div class="card">
            <div class="hero">
              <div class="eyebrow">Newsletter Preview</div>
              <h1 class="title">${title || "Untitled newsletter"}</h1>
              <p class="subject">${subject || "No subject provided"}</p>
            </div>
            <div class="content">
              ${
                content
                  ? content
                  : '<div class="empty"><strong>No content available</strong>This newsletter does not include saved HTML content.</div>'
              }
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const openNewsletterView = async (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    setViewError(null);
    setSelectedContent(newsletter.content || "");
    setIsViewOpen(true);

    if (newsletter.content) return;

    const newsletterId = getNewsletterId(newsletter);
    setViewLoading(true);
    try {
      const response = await newslettersApi.getNewsletterById(newsletterId);
      const content =
        response?.data?.content ??
        response?.data?.data?.content ??
        response?.data?.htmlContent ??
        response?.data?.newsletter?.content ??
        "";
      if (content) {
        setSelectedContent(content);
      } else {
        setViewError(
          response?.message || "This newsletter does not have stored HTML content."
        );
      }
    } catch (error: any) {
      setViewError(error?.message || "Unable to load newsletter content");
    } finally {
      setViewLoading(false);
    }
  };

  const closeNewsletterView = (open: boolean) => {
    setIsViewOpen(open);
    if (!open) {
      setSelectedContent("");
      setViewError(null);
      setViewLoading(false);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!isStatsOpen || !selectedNewsletter) return;

      const newsletterId = getNewsletterId(selectedNewsletter);
      setStatsLoading(true);
      setStatsError(null);

      try {
        const response = await newslettersApi.getNewsletterStats(newsletterId);
        if (response?.data) {
          setSelectedStats(response.data);
        } else {
          setSelectedStats(null);
          setStatsError(response?.message || "Unable to load newsletter stats");
        }
      } catch (error: any) {
        setSelectedStats(null);
        setStatsError(error?.message || "Unable to load newsletter stats");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [isStatsOpen, selectedNewsletter]);

  const statCards = [
    {
      label: "Total",
      value: selectedStats?.total ?? 0,
      icon: BarChart3,
      tone: "text-slate-700 bg-slate-100",
    },
    {
      label: "Sent",
      value: selectedStats?.sent ?? 0,
      icon: CircleCheckBig,
      tone: "text-emerald-700 bg-emerald-100",
    },
    {
      label: "Failed",
      value: selectedStats?.failed ?? 0,
      icon: CircleX,
      tone: "text-rose-700 bg-rose-100",
    },
    {
      label: "Opened",
      value: selectedStats?.opened ?? 0,
      icon: MailOpen,
      tone: "text-blue-700 bg-blue-100",
    },
    {
      label: "Clicked",
      value: selectedStats?.clicked ?? 0,
      icon: MousePointerClick,
      tone: "text-violet-700 bg-violet-100",
    },
  ];

  const renderPagination = () => {
    return renderPaginationControls(currentPage, totalPages, handlePageChange);
  };

  const renderPaginationControls = (
    activePage: number,
    activeTotalPages: number,
    onPageChange: (page: number) => void
  ) => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, activePage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(activeTotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={activePage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(activePage - 1)}
        disabled={activePage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={activePage === i ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(activePage + 1)}
        disabled={activePage === activeTotalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < activeTotalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(activeTotalPages)}
          disabled={activePage === activeTotalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-2">Newsletters</h1>
          <p className="text-muted-foreground">
            Manage your email campaigns and newsletters
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Newsletter
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by title or subject..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 border border-border shadow-sm"
              />
            </div>

            <Select value={limit.toString()} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-full sm:w-32 border border-border shadow-sm">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Newsletter List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading newsletters...</div>
            </div>
          ) : newsletters.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No newsletters found</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newsletters.map((newsletter) => (
                    <TableRow key={newsletter._id || newsletter.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">{newsletter.title}</TableCell>
                      <TableCell>{newsletter.subject}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          newsletter.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {newsletter.status ? newsletter.status.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDateTime12h(newsletter.createdAt)}
                      </TableCell>
                      <TableCell>
                        {newsletter.sentAt ? (
                          formatDateTime12h(newsletter.sentAt)
                        ) : (
                          <span className="text-muted-foreground">Not sent</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openNewsletterView(newsletter)}
                            className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openNewsletterStats(newsletter)}
                            className="bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                          >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            Stats
                          </Button>
                          {newsletter.status !== "sending" && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleSendNewsletter(getNewsletterId(newsletter), newsletter.title)}
                              disabled={sendingId === getNewsletterId(newsletter)}
                              className="bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {sendingId === getNewsletterId(newsletter)
                                ? "Sending..."
                                : (newsletter.status || "").toLowerCase() === "sent"
                                  ? "Resend"
                                  : "Send"}
                            </Button>
                          )}
                          {(newsletter.status || "").toLowerCase() !== "sending" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteNewsletter(newsletter)}
                              disabled={deletingId === getNewsletterId(newsletter)}
                              className="bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              {deletingId === getNewsletterId(newsletter)
                                ? "Deleting..."
                                : "Delete"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CreateNewsletterModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={afterCreateComplete}
      />

      <Dialog open={isStatsOpen} onOpenChange={setIsStatsOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Newsletter Stats
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-muted-foreground">Newsletter</div>
              <div className="font-medium">
                {selectedNewsletter?.title || "Selected newsletter"}
              </div>
              <div className="text-sm text-muted-foreground">
                {selectedNewsletter?.subject}
              </div>
            </div>

            {statsLoading ? (
              <div className="py-6 text-center text-muted-foreground">
                Loading stats...
              </div>
            ) : statsError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {statsError}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {statCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.label}
                      className="rounded-lg border bg-card p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-muted-foreground">
                            {card.label}
                          </div>
                          <div className="mt-1 text-2xl font-bold">
                            {card.value}
                          </div>
                        </div>
                        <div className={`rounded-full p-2 ${card.tone}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isViewOpen} onOpenChange={closeNewsletterView}>
        <DialogContent className="sm:max-w-5xl max-h-[calc(100vh-2rem)] overflow-hidden p-0">
          <div className="flex flex-col h-[calc(100vh-3rem)] sm:h-[calc(100vh-4rem)]">
            <DialogHeader className="px-4 sm:px-6 py-4 border-b border-border">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                View Newsletter
              </DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6">
              <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
                <div className="border-b border-border bg-gradient-to-r from-slate-50 to-blue-50 px-5 py-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                    Newsletter Preview
                  </div>
                  <h3 className="mt-2 text-2xl font-bold text-slate-900">
                    {selectedNewsletter?.title || "Untitled newsletter"}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {selectedNewsletter?.subject || "No subject provided"}
                  </p>
                </div>

                <div className="border-b border-border bg-white px-5 py-3 flex items-center justify-between gap-3">
                  <div className="text-sm text-muted-foreground">
                    {viewLoading ? "Loading saved HTML content..." : "Rendered HTML content"}
                  </div>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Read only
                  </span>
                </div>

                {viewLoading ? (
                  <div className="flex h-[56vh] items-center justify-center">
                    <div className="text-center">
                      <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                      <div className="text-sm font-medium text-slate-700">
                        Loading newsletter content...
                      </div>
                    </div>
                  </div>
                ) : viewError ? (
                  <div className="p-5">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      {viewError}
                    </div>
                  </div>
                ) : (
                  <iframe
                    title="Newsletter content preview"
                    srcDoc={buildNewsletterPreviewSrcDoc(
                      selectedNewsletter?.title || "",
                      selectedNewsletter?.subject || "",
                      selectedContent
                    )}
                    sandbox=""
                    className="h-[56vh] w-full bg-slate-50"
                  />
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
